import { useState } from 'react';
import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import { creators } from '@/mocks/creators';
import { formatDate } from '@/utils/format';
import type { SavedList } from '@/types/portal';

interface SavedListsPanelProps {
  lists: SavedList[];
  /** discovery.manage: rename, delete and change who is on a list. */
  canManage: boolean;
  /** Creators bookmarked on the result cards, which can be added to a list. */
  bookmarkedCount: number;
  busy: boolean;
  error: string;
  onClose: () => void;
  onLoad: (creatorIds: string[]) => void;
  onAddToCampaign: (creatorIds: string[]) => void;
  onCreate: () => void;
  onRename: (list: SavedList, name: string, notes: string) => Promise<boolean>;
  onDelete: (list: SavedList) => void;
  onRemoveMember: (list: SavedList, creatorId: string) => void;
  onAddBookmarked: (list: SavedList) => void;
}

const inputClass =
  'w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none';

export default function SavedListsPanel({
  lists,
  canManage,
  bookmarkedCount,
  busy,
  error,
  onClose,
  onLoad,
  onAddToCampaign,
  onCreate,
  onRename,
  onDelete,
  onRemoveMember,
  onAddBookmarked,
}: SavedListsPanelProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const startEdit = (list: SavedList) => {
    setDeletingId(null);
    setEditingId(list.id);
    setName(list.name);
    setNotes(list.notes ?? '');
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="absolute inset-0 bg-foreground-950/40 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside className="relative z-10 animate-slide-in-right flex h-full w-full max-w-md flex-col border-l border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-950">
              Saved lists
            </h3>
            <p className="text-xs text-foreground-500">Curated pools you can reuse</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 cursor-pointer hover:bg-background-100"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {error ? (
            <p role="alert" className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5" />
              {error}
            </p>
          ) : null}
          {lists.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-foreground-500">
              No saved lists yet. Run a search and use &ldquo;Save this search&rdquo; to build one.
            </p>
          ) : null}
          {lists.map((list) => {
            // Shortlists from the API carry their members; older local lists look them up in the mocks.
            const members =
              list.members ?? creators.filter((creator) => list.creatorIds.includes(creator.id));
            const editing = editingId === list.id;
            return (
              <div
                key={list.id}
                className="rounded-lg border border-background-200 bg-background-50 p-4"
              >
                {editing ? (
                  <div className="space-y-2">
                    <label className="block text-xs font-label font-medium text-foreground-700">
                      Name
                      <input
                        className={`${inputClass} mt-1 h-9`}
                        value={name}
                        maxLength={150}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </label>
                    <label className="block text-xs font-label font-medium text-foreground-700">
                      Notes
                      <textarea
                        className={`${inputClass} mt-1 py-2`}
                        rows={2}
                        maxLength={1000}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </label>
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        icon="ri-check-line"
                        disabled={!name.trim() || busy}
                        onClick={async () => {
                          if (await onRename(list, name.trim(), notes.trim())) setEditingId(null);
                        }}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground-950">{list.name}</p>
                      <p className="text-xs text-foreground-500">
                        {members.length} creators · updated {formatDate(list.updatedAt)}
                      </p>
                      {list.notes ? <p className="mt-1 text-xs text-foreground-600">{list.notes}</p> : null}
                    </div>
                    {canManage ? (
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => startEdit(list)}
                          aria-label={`Edit ${list.name}`}
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-900"
                        >
                          <i className="ri-edit-line" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setDeletingId(list.id);
                          }}
                          aria-label={`Delete ${list.name}`}
                          className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-primary-50 hover:text-primary-700"
                        >
                          <i className="ri-delete-bin-line" />
                        </button>
                      </div>
                    ) : null}
                  </div>
                )}

                {deletingId === list.id ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
                    <span className="text-xs text-primary-800">Delete this list? Creators stay in Discovery.</span>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => setDeletingId(null)}>
                        Keep
                      </Button>
                      <Button size="sm" disabled={busy} onClick={() => onDelete(list)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                ) : null}

                {members.length > 0 ? (
                  <ul className="mt-3 space-y-1.5">
                    {members.map((member) => (
                      <li key={member.id} className="flex items-center gap-2">
                        <Avatar src={member.avatar} alt={member.name} size="sm" />
                        <span className="min-w-0 flex-1 truncate text-xs text-foreground-800">{member.name}</span>
                        {canManage ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => onRemoveMember(list, member.id)}
                            aria-label={`Remove ${member.name} from ${list.name}`}
                            className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-primary-700 disabled:cursor-default"
                          >
                            <i className="ri-close-line" />
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon="ri-focus-3-line"
                    onClick={() => onLoad(list.creatorIds)}
                  >
                    Load in results
                  </Button>
                  {canManage && bookmarkedCount > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      icon="ri-bookmark-line"
                      disabled={busy}
                      onClick={() => onAddBookmarked(list)}
                    >
                      Add {bookmarkedCount} bookmarked
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    icon="ri-add-line"
                    onClick={() => onAddToCampaign(list.creatorIds)}
                  >
                    Start new campaign
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-background-200 p-5">
          <Button
            variant="outline"
            size="sm"
            icon="ri-add-line"
            className="w-full"
            onClick={onCreate}
          >
            Create new list
          </Button>
        </div>
      </aside>
    </div>
  );
}
